import { useEffect, useState } from 'react';
import { PaymentTerm, PaymentTermInput, PaymentTermResponse } from '@/types/schemas/payment-term.type';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

interface UsePaymentTermReturn {
  paymentTerms: PaymentTerm[];
  isLoading: boolean;
  addPaymentTerm: (newPaymentTerm: PaymentTermInput) => Promise<void>;
  updatePaymentTerm: (uuid: string, updatedPaymentTerm: PaymentTermInput) => Promise<void>;
  deletePaymentTerm: (uuid: string) => Promise<void>;
  refreshPaymentTerms: () => Promise<void>;
}

export const useHanThanhToan = (initialPaymentTerms: PaymentTerm[] = []): UsePaymentTermReturn => {
  const [paymentTerms, setPaymentTerms] = useState<PaymentTerm[]>(initialPaymentTerms);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { entity } = useAuth();

  const fetchPaymentTerms = async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.get<PaymentTermResponse>(`/entities/${entity.slug}/erp/payment-terms/`);
      setPaymentTerms(response.data.results);
    } catch (error) {
      console.error('Error fetching payment terms:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const addPaymentTerm = async (newPaymentTerm: PaymentTermInput) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.post(`/entities/${entity.slug}/erp/payment-terms/`, {
        ma_tt: newPaymentTerm.ma_tt,
        ten_tt: newPaymentTerm.ten_tt,
        ten_tt2: newPaymentTerm.ten_tt2 || '', // Sử dụng chuỗi rỗng thay vì null
        han_tt: newPaymentTerm.han_tt,
        status: newPaymentTerm.status
      });

      const addedPaymentTerm: PaymentTerm = response.data;
      setPaymentTerms(prev => [...prev, addedPaymentTerm]);
    } catch (error) {
      console.error('Error adding payment term:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updatePaymentTerm = async (uuid: string, updatedPaymentTerm: PaymentTermInput) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.put(`/entities/${entity.slug}/erp/payment-terms/${uuid}/`, {
        ma_tt: updatedPaymentTerm.ma_tt,
        ten_tt: updatedPaymentTerm.ten_tt,
        ten_tt2: updatedPaymentTerm.ten_tt2 || '', // Sử dụng chuỗi rỗng thay vì null
        han_tt: updatedPaymentTerm.han_tt,
        status: updatedPaymentTerm.status
      });

      // Update the payment term in the list
      const updatedPaymentTermData: PaymentTerm = response.data;

      setPaymentTerms(prev =>
        prev.map(term => (term.uuid === updatedPaymentTermData.uuid ? updatedPaymentTermData : term))
      );
    } catch (error) {
      console.error('Error updating payment term:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deletePaymentTerm = async (uuid: string) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/payment-terms/${uuid}/`);

      // Remove the payment term from the list
      setPaymentTerms(prev => prev.filter(term => term.uuid !== uuid));
    } catch (error) {
      console.error('Error deleting payment term:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentTerms();
  }, [entity?.slug]);

  return {
    paymentTerms,
    isLoading,
    addPaymentTerm,
    updatePaymentTerm,
    deletePaymentTerm,
    refreshPaymentTerms: fetchPaymentTerms
  };
};
